import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./app/App";
import "./app/AppliedPromotionSummaryHost.css";
import { AppliedPromotionSummaryHost } from "./app/AppliedPromotionSummaryHost";
import "./app/CartSwipe.css";
import "./app/CashOutRuntimeHost.css";
import { CashOutRuntimeHost } from "./app/CashOutRuntimeHost";
import "./app/CashSaleRuntimeHost.css";
import "./app/CashChangeHandoff.css";
import "./app/CashTenderPremium.css";
import "./app/PaymentSurfaceV122.css";
import { CashSaleRuntimeHost } from "./app/CashSaleRuntimeHost";
import { CashSaleVoidRuntimeHost } from "./app/CashSaleVoidRuntimeHost";
import "./app/CatalogRuntime.css";
import { CustomerBenefitRevalidationHost } from "./app/CustomerBenefitRevalidationHost";
import "./app/FinalPayablePresentation.css";
import { LocalProjectionRuntimeHost } from "./app/LocalProjectionRuntimeHost";
import { LocalStoreBootDiagnosticHost } from "./app/LocalStoreBootDiagnosticHost";
import { OriginCutoverDiagnosticHost } from "./app/OriginCutoverDiagnosticHost";
import "./app/LoyaltyPaymentContextHost.css";
import { LoyaltyPaymentContextHost } from "./app/LoyaltyPaymentContextHost";
import "./app/LoyaltyRedemptionRuntimeHost.css";
import { LoyaltyRedemptionRuntimeHost } from "./app/LoyaltyRedemptionRuntimeHost";
import "./app/MemberLinePairingRuntimeHost.css";
import { MemberReceivableHistoryHost } from "./app/MemberReceivableHistoryHost";
import "./app/ModifierRuntime.css";
import { ParkedCartRuntimeHost } from "./app/ParkedCartRuntimeHost";
import "./app/PosUpdatePrompt.css";
import { PosUpdatePrompt } from "./app/PosUpdatePrompt";
import "./app/PosWorkspace.css";
import "./app/SaleProductGridPerformance.css";
import "./app/SaleCompactUx.css";
import "./app/SaleUtilityMenuPremium.css";
import "./app/ProductMediaCardFooter.css";
import "./app/SaleContextRuntimeHost.css";
import { SaleContextMountHost } from "./app/SaleContextMountHost";
import { SaleContextRuntimeHost } from "./app/SaleContextRuntimeHost";
import { SaleUtilityMenuHost } from "./app/SaleUtilityMenuHost";
import { SecondaryReadRuntimeHost } from "./app/SecondaryReadRuntimeHost";
import { ReceivingRuntimeHost } from "./app/ReceivingRuntimeHost";
import { ReceivingVoidRuntimeHost } from "./app/ReceivingVoidRuntimeHost";
import "./app/SalesHistoryRuntimeHost.css";
import { SalesHistoryRuntimeHost } from "./app/SalesHistoryRuntimeHost";
import { ShiftCloseRuntimeHost } from "./app/ShiftCloseRuntimeHost";
import "./app/StockRuntimeHost.css";
import { StockRuntimeHost } from "./app/StockRuntimeHost";
import { StockTransferRuntimeHost } from "./app/StockTransferRuntimeHost";
import { registerHybridInteractionProbe } from "./runtime/diag/hybrid-interaction-probe";
import { employeeSessionClearedEventName } from "./runtime/local-store/employee-session";
import { readTerminalCredential } from "./runtime/local-store/terminal-state";
import { registerPosServiceWorker } from "./runtime/pwa/register";
import { registerTransientPosCredentialGuard } from "./runtime/pwa/transient-credential-guard";
import {
  recoverEmployeeSessionFromCore,
  recoverMissingEmployeeSessionBeforeAppBoot,
} from "./runtime/session/recover-missing-employee-session";
import { registerCartMutationVisibility } from "./runtime/ui/cart-mutation-visibility";
import { registerCashChangeHandoffPresentation } from "./runtime/ui/cash-change-handoff-presentation";
import { registerCashTenderInputAdapter } from "./runtime/ui/cash-tender-input-adapter";
import { registerPaymentSurfaceV122Presentation } from "./runtime/ui/payment-surface-v122-presentation";
import "./styles.css";
import "./app/OperationalViewport.css";

function registerEmployeeSessionSelfHealing() {
  let recoveryInFlight = false;

  const recoverAfterLocalLoss = () => {
    if (recoveryInFlight || !navigator.onLine) return;
    recoveryInFlight = true;

    void (async () => {
      const terminal = await readTerminalCredential();
      const recovered = await recoverEmployeeSessionFromCore(
        terminal,
        "LOCAL_EMPLOYEE_SESSION_INVALIDATED",
      );

      if (recovered) {
        // Re-enter through the ordinary Local First boot path using the recovered durable
        // session. This avoids mutating React state from a parallel bootstrap observer.
        window.location.reload();
      }
    })()
      .catch(() => undefined)
      .finally(() => {
        recoveryInFlight = false;
      });
  };

  window.addEventListener(employeeSessionClearedEventName, recoverAfterLocalLoss);
}

async function bootstrapPosRuntime() {
  const runtimeSearch = new URLSearchParams(window.location.search);

  // The interaction badge was introduced for T1 acceptance evidence only. Keep the
  // diagnostic available explicitly, but never mount it on the normal frontline UI.
  const interactionDiagnosticRequested =
    import.meta.env.DEV ||
    runtimeSearch.get("interaction-diagnostic") === "1";

  if (interactionDiagnosticRequested) {
    registerHybridInteractionProbe();
  }

  // Trusted Terminal is durable device authority and must be recovered before opening
  // rebuildable runtime projections. This also migrates legacy terminal:credential into
  // the dedicated trust DB before any projection schema upgrade can interfere with boot.
  const terminal = await readTerminalCredential();

  // Normal restart remains Local First: this helper makes no network request when a scoped
  // local employee session already exists. It only asks Access Core to recover the existing
  // ACTIVE session when local session state is missing while Trusted Terminal survived.
  // Failure to heal is non-destructive and falls through to the normal Employee Login gate.
  try {
    await recoverMissingEmployeeSessionBeforeAppBoot(terminal);
  } catch {
    // Never turn secondary Employee Session recovery into a Trusted Terminal boot failure.
  }

  // If a stale local token is rejected later by the canonical resolver, App clears only the
  // local session. Observe that lifecycle boundary and ask Access Core for the same ACTIVE
  // session before falling back to manual PIN. Explicit Logout remains safe because Core is
  // ended before Local is cleared, so recovery returns no active session.
  registerEmployeeSessionSelfHealing();

  registerTransientPosCredentialGuard();
  registerPosServiceWorker();
  registerCartMutationVisibility();
  registerCashTenderInputAdapter();
  registerPaymentSurfaceV122Presentation();
  registerCashChangeHandoffPresentation();

  // Production diagnostics are explicit and read-only. This surface never displays the
  // Device Secret and never mutates Trusted Terminal, Journal, Outbox, Session or Shift.
  const terminalDiagnosticRequested =
    import.meta.env.DEV ||
    runtimeSearch.get("terminal-diagnostic") === "1";

  // Origin cutover diagnostic is a separate explicit read-only surface. Plain `/` remains
  // unchanged; this reads only the current Trusted Terminal scope's Journal/Outbox state.
  const originCutoverDiagnosticRequested =
    runtimeSearch.get("origin-cutover-diagnostic") === "1";

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
      {terminalDiagnosticRequested ? <LocalStoreBootDiagnosticHost /> : null}
      {originCutoverDiagnosticRequested ? <OriginCutoverDiagnosticHost /> : null}
      <LocalProjectionRuntimeHost />
      <CustomerBenefitRevalidationHost />
      <SecondaryReadRuntimeHost />
      <ParkedCartRuntimeHost />
      <CashSaleRuntimeHost />
      <CashSaleVoidRuntimeHost />
      <ReceivingRuntimeHost />
      <ReceivingVoidRuntimeHost />
      <SaleUtilityMenuHost />
      {/* Secondary operational surfaces keep the critical sales workspace mounted underneath. */}
      <StockRuntimeHost />
      <StockTransferRuntimeHost />
      <SalesHistoryRuntimeHost />
      <CashOutRuntimeHost />
      <ShiftCloseRuntimeHost />
      <SaleContextMountHost />
      <SaleContextRuntimeHost />
      <LoyaltyRedemptionRuntimeHost />
      <AppliedPromotionSummaryHost />
      <MemberReceivableHistoryHost />
      <LoyaltyPaymentContextHost />
      <PosUpdatePrompt />
    </StrictMode>,
  );
}

void bootstrapPosRuntime();
